import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { SlaTimer } from '@/components/shared/SlaTimer';
import { toast } from 'sonner';
import { ticketsApi } from '@/lib/api';
import { MagnifyingGlass, Ticket as TicketIcon, User } from '@phosphor-icons/react';
import { formatRelativeTime } from '@/lib/utils';

export const FocAllTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedStatus !== 'all') params.status = selectedStatus;
      const { data } = await ticketsApi.list(params);
      setTickets(data.data || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, [selectedStatus]);

  const filteredTickets = tickets.filter(t =>
    t.ticket_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.product_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout role="foc">
      <div className="page-padding max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-black tracking-tight mb-2">All Tickets</h1>
          <p className="text-muted-foreground">View and manage all service requests</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex-1 relative min-w-[200px]">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tickets List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : filteredTickets.length > 0 ? (
          <div className="space-y-2">
            {filteredTickets.map((ticket) => (
              <Card
                key={ticket.id}
                className="p-4 hover:bg-muted/30 transition-all cursor-pointer border"
                onClick={() => navigate(`/ops/foc/tickets/${ticket.id}`)}
              >
                {/* Mobile: stacked */}
                <div className="flex flex-col gap-2 sm:hidden">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-sm">{ticket.ticket_number}</p>
                      <p className="text-xs text-muted-foreground truncate">{ticket.customer_name}</p>
                    </div>
                    <PriorityBadge priority={ticket.priority} />
                  </div>
                  <p className="text-sm font-medium truncate">{ticket.product_name}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={ticket.status} />
                    <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
                  </div>
                  {ticket.status === 'approved' && (
                    <Button size="sm" variant="outline" className="text-xs w-full mt-1">
                      <User size={14} className="mr-1" /> Assign
                    </Button>
                  )}
                </div>
                {/* Desktop: grid */}
                <div className="hidden sm:grid sm:grid-cols-12 sm:gap-3 sm:items-center">
                  <div className="col-span-3 min-w-0">
                    <p className="font-bold text-sm">{ticket.ticket_number}</p>
                    <p className="text-xs text-muted-foreground truncate">{ticket.customer_name}</p>
                  </div>
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-medium truncate">{ticket.product_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{ticket.issue_category}</p>
                  </div>
                  <div className="col-span-2">
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div className="col-span-1">
                    <PriorityBadge priority={ticket.priority} />
                  </div>
                  <div className="col-span-2">
                    <SlaTimer deadline={ticket.sla_deadline} status={ticket.status} />
                  </div>
                  <div className="col-span-1 text-right">
                    {ticket.status === 'approved' && (
                      <Button size="sm" variant="outline" className="text-xs">
                        <User size={14} className="mr-1" /> Assign
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center border">
            <TicketIcon size={48} className="mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground">No tickets found</p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};
